import '../../chat.css';

import { Badge } from '@kerfjs/ui/badge';
import { Button } from '@kerfjs/ui/button';
import { Row } from '@kerfjs/ui/row';
import { Text } from '@kerfjs/ui/text';
import type { SafeHtml } from 'kerfjs';

import { CurrencyAmount } from '../../../components/currency-amount.js';
import type { ChatMessageT } from '../../../data/api.js';
import { isRequester } from '../../../lib/context.js';
import { describeCard } from '../../../lib/helpers.js';
import { MessageMarkdown } from './message-markdown.js';

/** One rendered chat message (user bubble, or assistant with activity/cites/cards).
 *  In a read-only transcript, confirmation cards show status only (no actions). */
export function ChatMessageView({
  message: m,
  readOnly = false,
}: {
  message: ChatMessageT;
  readOnly?: boolean;
}): SafeHtml {
  // Form request/response/skip lines are threaded into the same conversation.
  const ctype = m.content.type;
  if (ctype === 'form_request' || ctype === 'form_response' || ctype === 'form_skipped') {
    // Questions are answered from the demand header's questions banner + dialog, not
    // in the assistant panel (HS2-P7B01F): the panel keeps only a compact status line
    // for the conversation record, never a duplicate answerable form card.
    const label =
      ctype === 'form_request'
        ? `Asked: ${m.content.title ?? 'a form'}`
        : ctype === 'form_response'
          ? `Answered: ${Object.entries(m.content.answers ?? {})
              .map(([k, v]) => `${k}=${String(v)}`)
              .join(', ')}`
          : 'Form skipped';
    // Literal classes per role keep the markup classifiable for Kerf's analyzer (KUI-L008).
    if (m.role === 'user')
      return (
        <li data-key={m.id} class="chat-message-view chat-message-view--user">
          {label}
        </li>
      );
    if (m.role === 'system')
      return (
        <li data-key={m.id} class="chat-message-view chat-message-view--system">
          {label}
        </li>
      );
    if (m.role === 'assistant')
      return (
        <li data-key={m.id} class="chat-message-view chat-message-view--assistant">
          {label}
        </li>
      );
    return (
      <li data-key={m.id} class="chat-message-view">
        {label}
      </li>
    );
  }
  if (m.role === 'user') {
    const text = typeof m.content.text === 'string' ? m.content.text : '';
    return (
      <li data-key={m.id} class="chat-message-view chat-message-view--user">
        <MessageMarkdown text={text} />
      </li>
    );
  }
  if (m.role !== 'assistant') {
    return (
      <li data-key={m.id} class="chat-message-view chat-message-view--system">
        {m.role}
      </li>
    );
  }
  const text = typeof m.content.text === 'string' ? m.content.text : '';
  const activity = m.content.activity ?? [];
  const citations = m.citations ?? [];
  const cards = m.createdEntities ?? [];
  return (
    <li data-key={m.id} class="chat-message-view chat-message-view--assistant">
      {activity.length > 0 ? (
        <ul class="chat-activity">
          {activity.map((a, i) => (
            <li data-key={`${m.id}-a${String(i)}`}>
              {a.detail !== undefined && a.detail !== '' ? `${a.label} · ${a.detail}` : a.label}
            </li>
          ))}
        </ul>
      ) : null}
      <MessageMarkdown text={text} />
      {m.content.uncited === true ? (
        <Badge tone="warning" appearance="solid" shape="pill">
          Unverified figure
        </Badge>
      ) : null}
      {citations.length > 0 ? (
        <ol class="chat-cites">
          {citations.map((c) => (
            <li data-key={`${m.id}-c${String(c.index)}`}>
              {c.kind === 'record' && c.demandId !== undefined ? (
                <a
                  href={`/?view=demand&id=${encodeURIComponent(c.demandId)}`}
                  data-action="open-demand-nav"
                  data-item-id={c.demandId}
                >
                  {c.demandTitle ?? 'Demand'}
                  {c.lineNumber !== undefined ? ` · line ${String(c.lineNumber)}` : null}
                  {c.recordType !== undefined ? ` · ${c.recordType}` : null}
                </a>
              ) : c.kind === 'record' && c.conversationId !== undefined ? (
                <a href={`/?view=chat&id=${encodeURIComponent(c.conversationId)}`}>
                  {c.conversationTitle ?? 'Conversation'} · conversation
                </a>
              ) : c.kind === 'document' ? (
                <span>📄 {c.documentTitle ?? 'document'}</span>
              ) : c.sourceUrl !== undefined ? (
                <a href={c.sourceUrl} target="_blank" rel="noopener noreferrer">
                  {c.sourceTitle ?? c.sourceUrl}
                </a>
              ) : (
                <span>{c.sourceTitle ?? 'benchmark'}</span>
              )}
              {c.price !== undefined && c.currency !== undefined ? (
                <Text variant="span" tone="quiet">
                  {' '}
                  — <CurrencyAmount value={c.price} currency={c.currency} />
                </Text>
              ) : null}
            </li>
          ))}
        </ol>
      ) : null}
      {cards.map((card) => (
        <div data-key={`${m.id}-card${String(card.index)}`} class="confirm-card" data-status={card.status}>
          <div>{describeCard(card)}</div>
          {card.status === 'pending' && !isRequester() && !readOnly ? (
            <Row className="cc-actions" hAlign="right" gap="xs" controlInsets="t">
              <Button
                tone="neutral"
                size="compact"
                emphasis="plain"
                rootAttributes={{ 'data-chat-dismiss': `${m.id}:${String(card.index)}` }}
              >
                Dismiss
              </Button>
              <Button
                size="compact"
                tone="brand"
                emphasis="primary"
                rootAttributes={{ 'data-chat-apply': `${m.id}:${String(card.index)}` }}
              >
                Confirm
              </Button>
            </Row>
          ) : (
            <Text variant="span" tone="quiet" size="compact">
              {card.status === 'pending' ? 'Awaiting a reviewer' : card.status}
            </Text>
          )}
        </div>
      ))}
    </li>
  );
}
